/**
 * Session state management for the Career Advisor.
 * Handles API calls and state tracking, returns data for the controller to render.
 */
import { createSession, sendMessage, sendMessageStreaming } from "./advisor_api"

export default class AdvisorSessionManager {
  constructor() {
    this.sessionId = null
    this.stage = null
    this.messagesRemaining = null
    this.sessionsRemaining = null
    this.sending = false
  }

  isReady() {
    return this.sessionId !== null && !this.sending
  }

  resume(sessionId, data) {
    this.sessionId = sessionId
    this.stage = data.stage
    this.messagesRemaining = data.messages_remaining
    if (data.sessions_remaining !== undefined) {
      this.sessionsRemaining = data.sessions_remaining
    }
  }

  async startSession(createUrl, entryContext, sessionType, persona) {
    const data = await createSession(createUrl, entryContext, sessionType, persona)

    if (!data.error) {
      this.sessionId = data.session_id
      this.stage = data.stage
      this.messagesRemaining = data.messages_remaining
      this.sessionsRemaining = data.sessions_remaining
    }

    return data
  }

  async sendMessage(messageUrl, text) {
    if (this.sending) return null
    this.sending = true

    try {
      const url = messageUrl.replace(":id", this.sessionId)
      const data = await sendMessage(url, text)

      if (!data.error) {
        this.messagesRemaining = data.messages_remaining
        this.stage = data.stage
        if (data.sessions_remaining !== undefined) {
          this.sessionsRemaining = data.sessions_remaining
        }
      }

      return data
    } finally {
      this.sending = false
    }
  }

  /**
   * Send a message via streaming. Falls back to non-streaming on error.
   * @param {string} streamUrl - The stream endpoint URL
   * @param {string} messageUrl - The fallback non-streaming URL
   * @param {string} text - The user's message
   * @param {Object} callbacks - SSE event handlers
   * @returns {Promise<Object|null>} Response data from stream_end, or fallback response
   */
  async sendMessageStream(streamUrl, messageUrl, text, callbacks) {
    if (this.sending) return null
    this.sending = true

    try {
      let streamEndData = null
      let streamFailed = false

      await sendMessageStreaming(streamUrl, this.sessionId, text, {
        onTextDelta: callbacks.onTextDelta,
        onToolResults: callbacks.onToolResults,
        onStatus: callbacks.onStatus,
        onStreamEnd: (data) => {
          streamEndData = data
          if (!data.error) {
            this.messagesRemaining = data.messages_remaining
            this.stage = data.stage
            if (data.sessions_remaining !== undefined) {
              this.sessionsRemaining = data.sessions_remaining
            }
          }
          if (callbacks.onStreamEnd) callbacks.onStreamEnd(data)
        },
        onBlocked: (data) => {
          if (callbacks.onBlocked) callbacks.onBlocked(data)
        },
        onError: (data) => {
          streamFailed = true
          if (callbacks.onError) callbacks.onError(data)
        }
      })

      // If streaming failed entirely, fall back to non-streaming
      if (streamFailed && !streamEndData) {
        return this._fallbackSendMessage(messageUrl, text)
      }

      return streamEndData
    } catch {
      // Network-level error — fall back to non-streaming
      return this._fallbackSendMessage(messageUrl, text)
    } finally {
      this.sending = false
    }
  }

  /** @private */
  async _fallbackSendMessage(messageUrl, text) {
    this.sending = false // Reset so sendMessage can acquire lock
    return this.sendMessage(messageUrl, text)
  }
}
